import { useMutation, useQuery } from '@apollo/client/react';
import { captureException } from '@sentry/react';
import { useCallback, useEffect } from 'react';

import { type BroadbandAppointment } from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  useTrackSignupChangeBroadbandAppointmentCompleted,
  useTrackSignupChangeBroadbandAppointmentConfirmed,
  useTrackSignupChangeBroadbandAppointmentFailed,
  useTrackSignupSuggestedBroadbandAppointmentChangeButtonClicked,
} from '@tracking/trackingEvents';
import { signupApplicationEntity } from '@tracking/trackingEvents';

import { ChangeAppointmentErrorModal } from './components/ChangeAppointmentErrorModal';
import { ChangeAppointmentModal } from './components/ChangeAppointmentModal';
import { InstallationAppointmentCard } from './components/InstallationAppointmentCard';
import { AppointmentStateKeys } from './constants';
import {
  availableBroadbandAppointments,
  reserveBroadbandAppointment,
  reservedBroadbandAppointment,
} from './query';
import { type SelectedAppointment } from './types';
import { isBroadbandAppointment } from './utils';

export const BroadbandInstallation = () => {
  const { basketId } = useBasketContext();
  const { applicationTrackingContext } = useTrackingContext();
  const { canUpdateState } = useAppointmentContext();

  const trackAppointmentSlotConfirmed =
    useTrackSignupChangeBroadbandAppointmentConfirmed();
  const trackAppointmentCompleted =
    useTrackSignupChangeBroadbandAppointmentCompleted();
  const trackAppointmentFailed =
    useTrackSignupChangeBroadbandAppointmentFailed();
  const trackSuggestedAppointmentChangeButtonClicked =
    useTrackSignupSuggestedBroadbandAppointmentChangeButtonClicked();

  const signupApplicationTrackingContext = applicationTrackingContext
    ? [signupApplicationEntity(applicationTrackingContext)]
    : [];

  const [isChangeAppointmentModalOpen, setIsChangeAppointmentModalOpen] =
    useSyncState(
      AppointmentStateKeys.BroadbandChangeAppointmentModalOpen,
      false
    );
  const [
    isChangeAppointmentErrorModalOpen,
    setIsChangeAppointmentErrorModalOpen,
  ] = useSyncState(
    AppointmentStateKeys.BroadbandChangeAppointmentErrorModalOpen,
    false
  );

  const { data: reservedAppointmentData, client } = useQuery(
    reservedBroadbandAppointment,
    {
      variables: {
        basketId,
      },
    }
  );

  const reservedAppointment =
    reservedAppointmentData?.reservedBroadbandAppointment;

  const {
    data: availableAppointmentsData,
    loading: availableAppointmentsLoading,
    refetch: refetchAvailableAppointments,
  } = useQuery(availableBroadbandAppointments, {
    variables: {
      basketId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const availableAppointments =
    availableAppointmentsData?.availableBroadbandAppointments;

  const [installationAppointment, setInstallationAppointment] =
    useSyncState<SelectedAppointment>(
      AppointmentStateKeys.BroadbandInstallationAppointment,
      {
        date: reservedAppointment?.date ?? null,
        slot: reservedAppointment?.slot ?? null,
      }
    );

  const [
    reserveAppointmentMutation,
    { loading: reserveAppointmentLoading, error: reserveAppointmentError },
  ] = useMutation(reserveBroadbandAppointment);

  const tryReserveAppointment = useCallback(
    async (appointment: BroadbandAppointment): Promise<boolean> => {
      const errorMessage = 'Failed to reserve broadband appointment';

      try {
        const { data } = await reserveAppointmentMutation({
          variables: {
            basketId,
            appointment: {
              date: appointment.date,
              slot: appointment.slot,
            },
          },
        });

        const reserved = data?.reserveBroadbandAppointment?.appointment;

        if (reserved) {
          // update the reserved appointment in the cache so that we can hit
          // the cache on page load/refresh and also on the thank you page
          client.writeQuery({
            query: reservedBroadbandAppointment,
            variables: { basketId },
            data: {
              reservedBroadbandAppointment: {
                __typename: 'BroadbandAppointment',
                date: reserved.date,
                slot: reserved.slot,
              },
            },
          });

          setInstallationAppointment({
            date: reserved.date,
            slot: reserved.slot,
          });

          return true;
        }

        return false;
      } catch (error) {
        captureException(new Error(errorMessage), {
          extra: {
            basketId,
            appointment,
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        });
        return false;
      }
    },
    [basketId, reserveAppointmentMutation] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // get the reserved appointment if it exists and set the installation appointment.
  // this prevents reserving a new appointment on page load/refresh if the basket has already reserved an appointment.
  // only the controller derives from their own cache; the non-controller waits for synced state.
  useEffect(() => {
    if (!canUpdateState) return;
    if (!isBroadbandAppointment(installationAppointment)) {
      if (reservedAppointment) {
        setInstallationAppointment(reservedAppointment);
      }
    }
  }, [canUpdateState, installationAppointment, reservedAppointment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Try to reserve an initial appointment,
  // retrying with the next appointment if the first one fails,
  // up to a maximum of three appointments.
  // only the controller drives reservation; the non-controller receives the result via synced state.
  useEffect(() => {
    if (!canUpdateState) return;
    if (!availableAppointments?.length || reservedAppointment) {
      return;
    }

    (async () => {
      for (const appointment of availableAppointments.slice(0, 3)) {
        const reserved = await tryReserveAppointment(appointment);

        if (reserved) {
          break;
        }
      }
    })();
  }, [
    canUpdateState,
    availableAppointments,
    reservedAppointment,
    tryReserveAppointment,
  ]);

  // Mirror synced installationAppointment into the Apollo cache on the non-controller,
  // so `reservedBroadbandAppointment` reads stay consistent after refresh / on the Thank You page
  // without replaying the reserve mutation from this side.
  useEffect(() => {
    if (canUpdateState) return;
    if (!basketId) return;
    if (!isBroadbandAppointment(installationAppointment)) return;

    client.writeQuery({
      query: reservedBroadbandAppointment,
      variables: { basketId },
      data: {
        reservedBroadbandAppointment: {
          __typename: 'BroadbandAppointment',
          date: installationAppointment.date,
          slot: installationAppointment.slot,
        },
      },
    });
  }, [canUpdateState, basketId, installationAppointment, client]);

  const handleClickChangeAppointment = () => {
    trackSuggestedAppointmentChangeButtonClicked(undefined, {
      contexts: signupApplicationTrackingContext,
    });

    setIsChangeAppointmentModalOpen(true);
  };

  const handleReloadAppointments = async () => {
    try {
      await refetchAvailableAppointments();
    } catch (error) {
      captureException(new Error('Failed to reload appointments'), {
        extra: {
          basketId,
          cause: error,
        },
      });
    }
  };

  const isAppointmentUnchanged = (appointment: SelectedAppointment) =>
    appointment.date === installationAppointment?.date &&
    appointment.slot === installationAppointment?.slot;

  const handleSelectAppointment = async (appointment: SelectedAppointment) => {
    if (!canUpdateState) return;

    if (
      !isBroadbandAppointment(appointment) ||
      isAppointmentUnchanged(appointment)
    ) {
      return;
    }

    trackAppointmentSlotConfirmed(
      {
        new_appointment_date: new Date(appointment.date).toISOString(),
        new_appointment_slot: appointment.slot,
        appointment_slot_was_suggested: appointment.suggested || false,
      },
      { contexts: signupApplicationTrackingContext }
    );

    const reserved = await tryReserveAppointment(appointment);

    if (reserved) {
      trackAppointmentCompleted(undefined, {
        contexts: signupApplicationTrackingContext,
      });
    } else {
      trackAppointmentFailed(undefined, {
        contexts: signupApplicationTrackingContext,
      });
      setIsChangeAppointmentErrorModalOpen(true);
    }
  };

  const shouldShowChangeAppointmentCard =
    !!availableAppointments?.length &&
    !!installationAppointment &&
    isBroadbandAppointment(installationAppointment);

  // The non-controller has no local reserveAppointmentError (they never fired the mutation),
  // so they rely solely on the synced isChangeAppointmentErrorModalOpen flag set by the controller.
  const shouldShowReserveAppointmentErrorModal =
    !isChangeAppointmentModalOpen &&
    isChangeAppointmentErrorModalOpen &&
    (!!reserveAppointmentError || !canUpdateState);

  if (!shouldShowChangeAppointmentCard) {
    return null;
  }

  return (
    <>
      <InstallationAppointmentCard
        date={installationAppointment?.date}
        slot={installationAppointment?.slot}
        onClickChangeAppointment={handleClickChangeAppointment}
      />
      <ChangeAppointmentModal
        loading={reserveAppointmentLoading}
        availableSlots={availableAppointments}
        open={isChangeAppointmentModalOpen}
        onClose={() => setIsChangeAppointmentModalOpen(false)}
        onSelectAppointment={handleSelectAppointment}
        selectedAppointment={installationAppointment}
        canConfirm={canUpdateState}
      />
      <ChangeAppointmentErrorModal
        open={shouldShowReserveAppointmentErrorModal}
        loading={availableAppointmentsLoading}
        onClose={() => setIsChangeAppointmentErrorModalOpen(false)}
        reloadAppointments={handleReloadAppointments}
      />
    </>
  );
};
